import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader, ImageUpload } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { title: "", description: "", image_file_id: null, cta_text: "", cta_url: "", start_date: "", end_date: "", active: true, order: 0 };

const offerStatus = (o) => {
  if (!o.active) return "inactive";
  const today = new Date().toISOString().slice(0, 10);
  if (o.start_date && o.start_date > today) return "scheduled";
  if (o.end_date && o.end_date < today) return "expired";
  return "active";
};
const statusCls = { active: "bg-brand-green/10 text-brand-green", scheduled: "bg-brand-cyan/10 text-brand-cyan", expired: "bg-amber-500/10 text-amber-400", inactive: "bg-slate-500/10 text-slate-400" };

export default function AdminOffers() {
  useSEO("Admin · Offers");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/offers").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/admin/offers/${editId}`, { ...form, order: Number(form.order) || 0 });
      else await api.post("/admin/offers", { ...form, order: Number(form.order) || 0 });
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/offers/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <AdminHeader title="Offers" subtitle="Manage promotional banners"
        action={<Button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="gradient-brand text-brand-navy font-semibold" data-testid="new-offer-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Offer</Button>} />
      <div className="grid sm:grid-cols-2 gap-4">
        {rows.length === 0 ? <p className="text-slate-500 col-span-full">No offers yet.</p>
          : rows.map((o) => (
            <div key={o.id} className="card-surface rounded-2xl overflow-hidden" data-testid={`admin-offer-${o.id}`}>
              {o.image_file_id && <img src={fileUrl(o.image_file_id)} alt={o.title} className="w-full h-32 object-cover" />}
              <div className="p-4 flex items-center justify-between">
                <div><p className="font-display font-semibold text-white">{o.title}</p>
                  <span className={`inline-block mt-1 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${statusCls[offerStatus(o)]}`} data-testid={`offer-status-${o.id}`}>{offerStatus(o)}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setForm({ ...empty, ...o }); setEditId(o.id); setOpen(true); }} className="text-slate-500 hover:text-brand-cyan"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(o.id)} className="text-slate-500 hover:text-red-400" data-testid={`del-offer-${o.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-surface border-white/10">
          <DialogHeader><DialogTitle className="text-white">{editId ? "Edit" : "Add"} Offer</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <ImageUpload value={form.image_file_id} onChange={(v) => setForm({ ...form, image_file_id: v })} label="Banner Image" />
            <div><Label className="text-slate-300">Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="offer-title" className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div><Label className="text-slate-300">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">CTA Text</Label><Input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
              <div><Label className="text-slate-300">CTA URL</Label><Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
              <div><Label className="text-slate-300">Start date (optional)</Label><Input type="date" value={(form.start_date || "").slice(0,10)} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="offer-start" className="mt-1.5 bg-white/5 border-white/10" /></div>
              <div><Label className="text-slate-300">End date (optional)</Label><Input type="date" value={(form.end_date || "").slice(0,10)} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="offer-end" className="mt-1.5 bg-white/5 border-white/10" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="offer-active" /> Active</label>
            <DialogFooter><Button type="submit" className="gradient-brand text-brand-navy font-semibold" data-testid="offer-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
